import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrashAlt,
  faPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import InlineFieldTooltip from "../components/InlineFieldTooltip";
import {
  D610_DEGRADATION_RATE_SETTING_KEY,
  D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY,
  D610_SCORE_METADATA_SETTING_KEY,
  D610_DEGRADATION_RATE_BANDS,
  normalizeD610DegradationRateMatrix,
  normalizeD610DegradationRateDefaultBand,
  normalizeD610ScoreMetadataMatrix,
} from "../utils/d610Degradation";

const parseSortOrder = (value, fallback = 100) => {
  if (value === "" || value === null || value === undefined) {
    return fallback;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeListName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const parseMultiValues = (input) => {
  const seen = new Set();
  return String(input || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => {
      const key = line.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const parseAttributeInput = (input) => {
  const seen = new Set();
  return String(input || "")
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .filter((line) => {
      const key = line.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const Settings = () => {
  const DECIMAL_SETTINGS_KEYS = new Set([
    "default_initial_degradation",
    "degradation_reset_value",
    "default_inflation_rate_pct",
    "default_layers_per_day",
    "default_confined_space_multiplier",
  ]);

  const [activeTab, setActiveTab] = useState("currency_units");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Dynamic configuration state
  const [coatingSystems, setCoatingSystems] = useState([]);
  const [degradationParams, setDegradationParams] = useState([]);
  const [showCoatingModal, setShowCoatingModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [coatingFormData, setCoatingFormData] = useState({
    system_code: "",
    system_name: "",
    description: "",
    coat_sequence: ["prime", "topcoat"],
    is_active: true,
  });

  // Currency Conversion Rates state
  const [conversionRates, setConversionRates] = useState([]);
  const [conversionRatesLoading, setConversionRatesLoading] = useState(false);
  const [showConversionRateModal, setShowConversionRateModal] = useState(false);
  const [editingConversionRate, setEditingConversionRate] = useState(null);
  const [conversionRateFormData, setConversionRateFormData] = useState({
    year: new Date().getFullYear(),
    from_currency: "USD",
    to_currency: "CAD",
    conversion_rate: "",
    is_active: true,
  });

  // System Lists state
  const [systemListNames, setSystemListNames] = useState([]);
  const [activeSystemList, setActiveSystemList] = useState("");
  const [activeSystemListId, setActiveSystemListId] = useState("");
  const [activeSystemListKey, setActiveSystemListKey] = useState("");
  const [systemListValues, setSystemListValues] = useState([]);
  const [systemListsLoading, setSystemListsLoading] = useState(false);
  const [showSystemListCreateModal, setShowSystemListCreateModal] =
    useState(false);
  const [showSystemListRenameModal, setShowSystemListRenameModal] =
    useState(false);
  const [showSystemListSchemaModal, setShowSystemListSchemaModal] =
    useState(false);
  const [showSystemListValueModal, setShowSystemListValueModal] =
    useState(false);
  const [systemListValueModalMode, setSystemListValueModalMode] =
    useState("add");
  const [editingSystemListValue, setEditingSystemListValue] = useState(null);
  const [newSystemListName, setNewSystemListName] = useState("");
  const [renameSourceListId, setRenameSourceListId] = useState("");
  const [renameSourceListName, setRenameSourceListName] = useState("");
  const [renameTargetListName, setRenameTargetListName] = useState("");
  const [systemListValueInput, setSystemListValueInput] = useState("");
  const [newSystemListSortOrder, setNewSystemListSortOrder] = useState(100);
  const [newSystemListAttributeInput, setNewSystemListAttributeInput] =
    useState("");
  const [activeSystemListAttributes, setActiveSystemListAttributes] = useState(
    [],
  );
  const [systemListSchemaInput, setSystemListSchemaInput] = useState("");
  const [systemListPropertyValues, setSystemListPropertyValues] = useState({});

  const tabs = [
    { id: "currency_units", name: "Currency & Units", icon: "faDollarSign" },
    { id: "system_lists", name: "System Lists", icon: "faList" },
    {
      id: "project_defaults",
      name: "Project Defaults",
      icon: "faProjectDiagram",
    },
    {
      id: "d610_degradation_rate",
      name: "D610 Degradation Rate",
      icon: "faTable",
    },
  ];

  useEffect(() => {
    fetchSettings();
    if (activeTab === "currency_units") {
      fetchConversionRates();
    } else if (activeTab === "system_lists") {
      fetchSystemListNames();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== "system_lists") return;
    if (!activeSystemList) {
      setSystemListValues([]);
      return;
    }
    fetchSystemListValues({
      listId: activeSystemListId,
      listName: activeSystemList,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activeSystemList, activeSystemListId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get("/settings/categories");
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      setMessage("Error loading settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchCoatingSystems = async () => {
    try {
      const response = await api.get("/dynamic-config/coating-systems");
      if (response.data.success) {
        setCoatingSystems(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching coating systems:", error);
      setMessage("Error loading coating systems");
    }
  };

  const fetchDegradationParams = async () => {
    try {
      const response = await api.get("/dynamic-config/degradation-parameters");
      if (response.data.success) {
        setDegradationParams(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching degradation parameters:", error);
      setMessage("Error loading degradation parameters");
    }
  };

  const fetchConversionRates = async () => {
    try {
      setConversionRatesLoading(true);
      const response = await api.get("/currency-conversion-rates");
      if (response.data.success) {
        setConversionRates(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching conversion rates:", error);
      setMessage("Error loading currency conversion rates");
    } finally {
      setConversionRatesLoading(false);
    }
  };

  const saveSettings = async (categoryData) => {
    try {
      setSaving(true);
      const response = await api.put(
        `/settings/categories/${activeTab}`,
        categoryData,
      );
      if (response.data.success) {
        setMessage("Settings saved successfully");
        setTimeout(() => setMessage(""), 3000);
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      setMessage("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: {
          ...prev[activeTab][key],
          value: value,
        },
      },
    }));
  };

  const handleSave = () => {
    if (activeTab === "system_lists") {
      setMessage("System Lists are saved per action.");
      setTimeout(() => setMessage(""), 2000);
      return;
    }

    const categorySettings = settings[activeTab] || {};
    const categoryData = Object.entries(categorySettings).reduce(
      (acc, [key, settingData]) => {
        if (settingData?.isEditable === false) {
          return acc;
        }
        acc[key] = settingData?.value;
        return acc;
      },
      {},
    );

    saveSettings(categoryData);
  };

  const handleD610DegradationRateChange = (score, field, nextValue) => {
    setSettings((prev) => {
      const previousCategory = prev.d610_degradation_rate || {};
      const previousSetting = previousCategory[D610_DEGRADATION_RATE_SETTING_KEY];
      const nextMatrix = normalizeD610DegradationRateMatrix(
        previousSetting?.value,
      ).map((row) =>
        row.score === score ? { ...row, [field]: nextValue } : row,
      );

      return {
        ...prev,
        d610_degradation_rate: {
          ...previousCategory,
          [D610_DEGRADATION_RATE_SETTING_KEY]: {
            value: nextMatrix,
            type: previousSetting?.type || "json",
            description:
              previousSetting?.description ||
              "Lower, mid, and upper D610 degradation rate bands by score.",
            isEditable: previousSetting?.isEditable ?? true,
          },
        },
      };
    });
  };

  const handleD610DefaultBandChange = (nextBand) => {
    const normalizedBand = normalizeD610DegradationRateDefaultBand(nextBand);

    setSettings((prev) => {
      const previousCategory = prev.d610_degradation_rate || {};
      const previousSetting =
        previousCategory[D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY];

      return {
        ...prev,
        d610_degradation_rate: {
          ...previousCategory,
          [D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY]: {
            value: normalizedBand,
            type: previousSetting?.type || "string",
            description:
              previousSetting?.description ||
              "Default D610 degradation rate band to use (lower, mid, or upper).",
            isEditable: previousSetting?.isEditable ?? true,
          },
        },
      };
    });
  };

  const handleD610ScoreMetadataChange = (score, field, nextValue) => {
    setSettings((prev) => {
      const previousCategory = prev.d610_degradation_rate || {};
      const previousSetting = previousCategory[D610_SCORE_METADATA_SETTING_KEY];
      const nextMatrix = normalizeD610ScoreMetadataMatrix(
        previousSetting?.value,
      ).map((row) =>
        row.score === score ? { ...row, [field]: nextValue } : row,
      );

      return {
        ...prev,
        d610_degradation_rate: {
          ...previousCategory,
          [D610_SCORE_METADATA_SETTING_KEY]: {
            value: nextMatrix,
            type: previousSetting?.type || "json",
            description:
              previousSetting?.description ||
              "SSLG grade, Budget App naming, abbreviation, and colour mapping for each D610 score.",
            isEditable: previousSetting?.isEditable ?? true,
          },
        },
      };
    });
  };

  const fetchSystemListNames = useCallback(async () => {
    try {
      setSystemListsLoading(true);
      const response = await api.get("/system-lists/lists");
      if (response.data.success) {
        const names = (response.data.data || [])
          .map((row) => ({
            list: String(row.list || "").trim(),
            list_id: String(row.list_id || "").trim(),
            list_key: String(row.list_key || "").trim(),
          }))
          .filter((row) => row.list && row.list_id)
          .sort((a, b) => a.list.localeCompare(b.list));

        setSystemListNames(names);

        const activeStillExists = names.find(
          (row) => row.list_id === activeSystemListId,
        );
        if (activeStillExists) {
          setActiveSystemList(activeStillExists.list);
          setActiveSystemListKey(activeStillExists.list_key);
          return;
        }

        if (activeSystemList) {
          const byName = names.find(
            (row) =>
              normalizeListName(row.list) ===
              normalizeListName(activeSystemList),
          );
          if (byName) {
            setActiveSystemList(byName.list);
            setActiveSystemListId(byName.list_id);
            setActiveSystemListKey(byName.list_key);
            return;
          }
        }

        if (names.length > 0) {
          setActiveSystemList(names[0].list);
          setActiveSystemListId(names[0].list_id);
          setActiveSystemListKey(names[0].list_key);
        }
      }
    } catch (error) {
      console.error("Error loading system list names:", error);
      setMessage("Error loading system lists");
    } finally {
      setSystemListsLoading(false);
    }
  }, [activeSystemList, activeSystemListId]);

  const fetchSystemListValues = useCallback(async ({ listId, listName }) => {
    if (!listName) {
      setSystemListValues([]);
      return;
    }

    try {
      setSystemListsLoading(true);
      const response = await api.get("/system-lists", {
        params: {
          ...(listId ? { list_id: listId } : { list: listName }),
          limit: 500,
        },
      });
      if (response.data.success) {
        setSystemListValues(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading system list values:", error);
      setMessage("Error loading list values");
    } finally {
      setSystemListsLoading(false);
    }
  }, []);

  const isMaterialTypeList =
    activeSystemListKey === "material_type" ||
    normalizeListName(activeSystemList) === "material type";

  const fetchSystemListSchema = useCallback(async ({ listId, listName }) => {
    if (!listName) {
      setActiveSystemListAttributes([]);
      return;
    }

    try {
      const response = await api.get("/system-lists/schema", {
        params: listId ? { list_id: listId } : { list: listName },
      });
      if (response.data.success) {
        const attrs = Array.isArray(response.data?.data?.attributes)
          ? response.data.data.attributes
          : [];
        setActiveSystemListAttributes(attrs);
      }
    } catch (error) {
      console.error("Error loading list schema:", error);
      setActiveSystemListAttributes([]);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "system_lists") return;
    fetchSystemListSchema({
      listId: activeSystemListId,
      listName: activeSystemList,
    });
  }, [activeTab, activeSystemList, activeSystemListId, fetchSystemListSchema]);

  const openCreateSystemListModal = () => {
    setNewSystemListName("");
    setSystemListValueInput("");
    setNewSystemListSortOrder(100);
    setNewSystemListAttributeInput("");
    setShowSystemListCreateModal(true);
  };

  const openRenameSystemListModal = (listItem) => {
    const listName = String(listItem?.list || "").trim();
    const name = String(listName || "").trim();
    if (!name) return;
    setRenameSourceListId(String(listItem?.list_id || "").trim());
    setRenameSourceListName(name);
    setRenameTargetListName(name);
    setShowSystemListRenameModal(true);
  };

  const handleRenameSystemList = async () => {
    const sourceListId = String(renameSourceListId || "").trim();
    const oldListName = String(renameSourceListName || "").trim();
    const newListName = String(renameTargetListName || "").trim();

    if ((!sourceListId && !oldListName) || !newListName) {
      setMessage("Both current and new list name are required");
      return;
    }

    if (normalizeListName(oldListName) === normalizeListName(newListName)) {
      setMessage("New list name must be different");
      return;
    }

    const existing = (systemListNames || []).find(
      (item) =>
        item.list_id !== sourceListId &&
        normalizeListName(item.list) === normalizeListName(newListName),
    );
    if (existing) {
      setMessage(`List '${existing.list}' already exists`);
      return;
    }

    try {
      await api.put("/system-lists/lists/rename", {
        list_id: sourceListId,
        old_list: oldListName,
        new_list: newListName,
      });

      await fetchSystemListNames();
      if (activeSystemListId === sourceListId) {
        setActiveSystemList(newListName);
      }
      await fetchSystemListValues({
        listId: sourceListId,
        listName: newListName,
      });
      await fetchSystemListSchema({
        listId: sourceListId,
        listName: newListName,
      });

      setShowSystemListRenameModal(false);
      setRenameSourceListId("");
      setRenameSourceListName("");
      setRenameTargetListName("");
      setMessage("List renamed successfully");
    } catch (error) {
      console.error("Error renaming list:", error);
      setMessage(error.response?.data?.message || "Error renaming list");
    }
  };

  const openSystemListSchemaModal = () => {
    setSystemListSchemaInput((activeSystemListAttributes || []).join("\n"));
    setShowSystemListSchemaModal(true);
  };

  const handleCreateSystemList = async () => {
    const trimmedListName = String(newSystemListName || "").trim();
    const values = parseMultiValues(systemListValueInput);

    if (!trimmedListName) {
      setMessage("List name is required");
      return;
    }
    if (values.length === 0) {
      setMessage("Add at least one value");
      return;
    }

    const existingList = (systemListNames || []).find(
      (item) =>
        normalizeListName(item.list) === normalizeListName(trimmedListName),
    );
    if (existingList) {
      setMessage(`List '${existingList.list}' already exists`);
      return;
    }

    const requestedAttributes = parseAttributeInput(
      newSystemListAttributeInput,
    );
    const schemaAttributes =
      normalizeListName(trimmedListName) === "material type"
        ? Array.from(new Set([...requestedAttributes, "color"]))
        : requestedAttributes;

    const defaultProperties = schemaAttributes.reduce((acc, key) => {
      acc[key] = "";
      return acc;
    }, {});

    try {
      await api.post("/system-lists", {
        list: trimmedListName,
        values,
        sort_order: parseSortOrder(newSystemListSortOrder, 100),
        properties: defaultProperties,
      });

      if (schemaAttributes.length > 0) {
        await api.put("/system-lists/schema", {
          list: trimmedListName,
          attributes: schemaAttributes,
        });
      }

      await fetchSystemListNames();
      setActiveSystemList(trimmedListName);
      setActiveSystemListAttributes(schemaAttributes);
      setShowSystemListCreateModal(false);
      setMessage("System list created successfully");
    } catch (error) {
      console.error("Error creating system list:", error);
      setMessage(error.response?.data?.message || "Error creating system list");
    }
  };

  const handleEditSystemListValue = async (item) => {
    setSystemListValueModalMode("edit");
    setEditingSystemListValue(item);
    setSystemListValueInput(String(item.value || ""));
    setNewSystemListSortOrder(
      parseSortOrder(item.sortOrder ?? item.sort_order, 100),
    );
    const nextPropertyValues = (activeSystemListAttributes || []).reduce(
      (acc, key) => {
        const rawValue = item?.properties?.[key];
        acc[key] = rawValue !== undefined && rawValue !== null ? rawValue : "";
        return acc;
      },
      {},
    );
    setSystemListPropertyValues(nextPropertyValues);
    setShowSystemListValueModal(true);
  };

  const handleSubmitSystemListValueModal = async () => {
    if (!activeSystemList) {
      setMessage("Select a list first");
      return;
    }
    const values = parseMultiValues(systemListValueInput);
    if (values.length === 0) {
      setMessage("Add at least one value");
      return;
    }

    const properties = (activeSystemListAttributes || []).reduce((acc, key) => {
      acc[key] =
        systemListPropertyValues[key] !== undefined
          ? systemListPropertyValues[key]
          : "";
      return acc;
    }, {});

    try {
      if (systemListValueModalMode === "edit" && editingSystemListValue) {
        const nextValue = values[0];
        await api.put(`/system-lists/${editingSystemListValue.id}`, {
          list_id: activeSystemListId,
          value: nextValue,
          sort_order: parseSortOrder(newSystemListSortOrder, 100),
          properties,
        });
        setMessage("List value updated successfully");
      } else {
        await api.post("/system-lists", {
          list_id: activeSystemListId,
          list: activeSystemList,
          values,
          sort_order: parseSortOrder(newSystemListSortOrder, 100),
          properties,
        });
        setMessage("List values added successfully");
      }

      setSystemListValueInput("");
      setNewSystemListSortOrder(100);
      setSystemListPropertyValues({});
      setEditingSystemListValue(null);
      setShowSystemListValueModal(false);
      await fetchSystemListValues({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
      await fetchSystemListNames();
      await fetchSystemListSchema({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
    } catch (error) {
      console.error("Error saving list value:", error);
      setMessage(error.response?.data?.message || "Error saving list value");
    }
  };

  const handleSaveSystemListSchema = async () => {
    if (!activeSystemList) {
      setMessage("Select a list first");
      return;
    }

    const requestedAttributes = parseAttributeInput(systemListSchemaInput);
    const schemaAttributes = isMaterialTypeList
      ? Array.from(new Set([...requestedAttributes, "color"]))
      : requestedAttributes;

    try {
      await api.put("/system-lists/schema", {
        list_id: activeSystemListId,
        list: activeSystemList,
        attributes: schemaAttributes,
      });

      await fetchSystemListSchema({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
      await fetchSystemListValues({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
      setShowSystemListSchemaModal(false);
      setMessage("List attributes updated successfully");
    } catch (error) {
      console.error("Error saving list schema:", error);
      setMessage(error.response?.data?.message || "Error saving list schema");
    }
  };

  const handleDeleteSystemListValue = async (item) => {
    if (!window.confirm(`Delete "${item.value}" from ${item.list}?`)) return;
    try {
      await api.delete(`/system-lists/${item.id}`);
      await fetchSystemListValues({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
      await fetchSystemListNames();
      await fetchSystemListSchema({
        listId: activeSystemListId,
        listName: activeSystemList,
      });
      setMessage("List value deleted successfully");
    } catch (error) {
      console.error("Error deleting list value:", error);
      setMessage(error.response?.data?.message || "Error deleting list value");
    }
  };

  const handleDeleteSystemList = async (listItem) => {
    const trimmedListName = String(listItem?.list || "").trim();
    const listId = String(listItem?.list_id || "").trim();
    if (!trimmedListName) return;

    const confirmed = window.confirm(
      `Delete list '${trimmedListName}' and all its values?`,
    );
    if (!confirmed) return;

    try {
      await api.delete(
        `/system-lists/lists/${encodeURIComponent(trimmedListName)}?list_id=${encodeURIComponent(listId)}`,
      );

      if (activeSystemListId === listId) {
        setActiveSystemList("");
        setActiveSystemListId("");
        setActiveSystemListKey("");
        setSystemListValues([]);
        setActiveSystemListAttributes([]);
      }

      await fetchSystemListNames();
      setMessage("List deleted successfully");
    } catch (error) {
      console.error("Error deleting list:", error);
      setMessage(error.response?.data?.message || "Error deleting list");
    }
  };

  const filteredSystemListNames = useMemo(() => {
    return [...systemListNames].sort((a, b) => a.list.localeCompare(b.list));
  }, [systemListNames]);

  const renderSystemListsTab = () => {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Lists</h3>
              <InlineFieldTooltip
                page="settings"
                section="system_lists"
                fieldKey="system_lists_lists_panel"
                fallbackText="Manage available system lists."
                title="System Lists"
              />
            </div>
            <button
              onClick={openCreateSystemListModal}
              className="text-blue-600 hover:text-blue-800"
              title="Add list"
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {filteredSystemListNames.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500">
                No lists yet. Use + to create one.
              </div>
            ) : (
              filteredSystemListNames.map((listItem) => (
                <div
                  key={listItem.list_id}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                    activeSystemListId === listItem.list_id
                      ? "bg-blue-50 text-blue-700"
                      : "text-gray-800"
                  }`}
                >
                  <button
                    onClick={() => {
                      setActiveSystemList(listItem.list);
                      setActiveSystemListId(listItem.list_id);
                      setActiveSystemListKey(listItem.list_key);
                    }}
                    className="text-left flex-1"
                  >
                    {listItem.list}
                  </button>
                  <button
                    type="button"
                    onClick={() => openRenameSystemListModal(listItem)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    title="Rename list"
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteSystemList(listItem)}
                    className="ml-2 text-red-600 hover:text-red-800"
                    title="Delete list"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-8 bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">
                  {activeSystemList || "Select a list"}
                </h3>
                <InlineFieldTooltip
                  page="settings"
                  section="system_lists"
                  fieldKey="system_lists_selected_list_panel"
                  fallbackText="Manage values and attributes for the selected list."
                  title="Selected List"
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Manage values for the selected list.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={openSystemListSchemaModal}
                disabled={!activeSystemList}
                className="px-3 py-2 border border-blue-300 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit Attributes
              </button>
              <button
                onClick={() => {
                  if (!activeSystemList) return;
                  setSystemListValueModalMode("add");
                  setEditingSystemListValue(null);
                  setSystemListValueInput("");
                  setNewSystemListSortOrder(100);
                  setSystemListPropertyValues(
                    (activeSystemListAttributes || []).reduce((acc, key) => {
                      acc[key] = "";
                      return acc;
                    }, {}),
                  );
                  setShowSystemListValueModal(true);
                }}
                disabled={!activeSystemList}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                Add New Value
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {systemListsLoading ? (
              <div className="p-6 text-center text-gray-500">
                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                Loading list values...
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attributes
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sort Order
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {systemListValues.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.value}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {activeSystemListAttributes.length > 0 ? (
                          <div className="space-y-1">
                            {activeSystemListAttributes.map((key) => {
                              const propValue = item?.properties?.[key] ?? "";
                              return (
                                <div
                                  key={key}
                                  className="flex items-center gap-2"
                                >
                                  <span className="text-xs text-gray-500">
                                    {key}:
                                  </span>
                                  {String(key).toLowerCase() === "color" ? (
                                    <span className="inline-flex items-center gap-2">
                                      <span
                                        className="inline-block h-3 w-3 rounded border border-gray-300"
                                        style={{
                                          backgroundColor:
                                            String(propValue || "") ||
                                            "transparent",
                                        }}
                                      />
                                      <span>{String(propValue || "")}</span>
                                    </span>
                                  ) : (
                                    <span>{String(propValue || "")}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {parseSortOrder(item.sortOrder ?? item.sort_order, 100)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => handleEditSystemListValue(item)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                          title="Edit value"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => handleDeleteSystemListValue(item)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete value"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!systemListsLoading && systemListValues.length === 0 && (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-4 py-6 text-center text-sm text-gray-500"
                      >
                        No values found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getSettingDisplayName = (key) => {
    const customLabels = {
      default_foremans_count: "# Foremen",
      default_layers_per_day: "#Layers per Day",
      default_confined_space_multiplier: "Confined Space Multiplier",
    };

    if (customLabels[key]) {
      return customLabels[key];
    }

    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderLabelWithTooltip = ({
    label,
    fieldKey,
    section = activeTab,
    htmlFor,
    className = "text-sm font-medium text-gray-700",
  }) => (
    <div className="mb-1 flex items-center gap-2">
      <label htmlFor={htmlFor} className={className}>
        {label}
      </label>
      <InlineFieldTooltip
        page="settings"
        section={section}
        fieldKey={fieldKey}
      />
    </div>
  );

  const renderSettingField = (key, settingData) => {
    if (!settingData) {
      return null;
    }

    const { value, type, description, isEditable } = settingData;
    const readOnly = isEditable === false;

    const renderInput = () => {
      if (readOnly) {
        return (
          <input
            type="text"
            value={value ?? ""}
            readOnly
            className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-700"
          />
        );
      }

      if (key.includes("currency")) {
        return (
          <select
            value={value || ""}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="CAD">CAD</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
          </select>
        );
      }

      if (key.includes("unit")) {
        return (
          <select
            value={value || ""}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="m²">m² (Square Meters)</option>
            <option value="ft²">ft² (Square Feet)</option>
          </select>
        );
      }

      if (key.includes("mobilization_method")) {
        return (
          <select
            value={value || ""}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="weekly_scaffolding">Weekly Scaffolding</option>
            <option value="fixed_cost">Fixed Cost</option>
          </select>
        );
      }

      if (type === "boolean") {
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(key, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">Enabled</span>
          </div>
        );
      }

      if (type === "number") {
        const step = DECIMAL_SETTINGS_KEYS.has(key) ? "0.01" : "1";
        return (
          <input
            type="number"
            step={step}
            value={value ?? ""}
            onChange={(e) => {
              const inputValue = e.target.value;
              handleInputChange(
                key,
                inputValue === "" ? "" : Number(inputValue),
              );
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );
      }

      return (
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => handleInputChange(key, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      );
    };

    return (
      <div
        key={key}
        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-2 flex items-center gap-2">
              <label className="text-sm font-semibold text-gray-900">
                {getSettingDisplayName(key)}
              </label>
              <InlineFieldTooltip
                page="settings"
                section={activeTab}
                fieldKey={`${activeTab}.${key}`}
              />
            </div>
            {description && (
              <p className="text-sm text-gray-600 mb-2 leading-relaxed">
                {description}
              </p>
            )}
            {renderInput()}
          </div>
          {readOnly && (
            <div className="ml-4 flex-shrink-0">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <i className="fas fa-lock mr-1"></i>
                Read Only
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dynamic configuration CRUD handlers
  const handleDeleteCoatingSystem = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this coating system?")
    ) {
      try {
        await api.delete(`/dynamic-config/coating-systems/${id}`);
        fetchCoatingSystems();
        setMessage("Coating system deleted successfully");
      } catch (error) {
        console.error("Error deleting coating system:", error);
        setMessage("Error deleting coating system");
      }
    }
  };

  const handleEditDegradationParam = async (param) => {
    // Use a more user-friendly approach
    const newCoeff = window.prompt(
      `Edit Coefficient A for ${param.corrosion_category}:`,
      param.coefficient_a,
    );
    if (newCoeff === null) return; // User cancelled

    const newRate = window.prompt(
      `Edit Growth Rate B for ${param.corrosion_category}:`,
      param.growth_rate_b,
    );
    if (newRate === null) return; // User cancelled

    if (
      newCoeff &&
      newRate &&
      !isNaN(parseFloat(newCoeff)) &&
      !isNaN(parseFloat(newRate))
    ) {
      try {
        await api.put(`/dynamic-config/degradation-parameters/${param.id}`, {
          coefficient_a: parseFloat(newCoeff),
          growth_rate_b: parseFloat(newRate),
        });
        fetchDegradationParams();
        setMessage(
          `Degradation parameters for ${param.corrosion_category} updated successfully`,
        );
      } catch (error) {
        console.error("Error updating degradation parameter:", error);
        setMessage("Error updating degradation parameter");
      }
    } else {
      setMessage("Invalid input. Please enter valid numbers.");
    }
  };

  const handleSaveCoatingSystem = async () => {
    try {
      if (editingItem) {
        await api.put(
          `/dynamic-config/coating-systems/${editingItem.id}`,
          coatingFormData,
        );
      } else {
        await api.post("/dynamic-config/coating-systems", coatingFormData);
      }
      setShowCoatingModal(false);
      setEditingItem(null);
      fetchCoatingSystems();
      setMessage("Coating system saved successfully");
    } catch (error) {
      console.error("Error saving coating system:", error);
      setMessage("Error saving coating system");
    }
  };

  // Currency Conversion Rates handlers
  const handleSaveConversionRate = async () => {
    try {
      if (editingConversionRate) {
        await api.put(
          `/currency-conversion-rates/${editingConversionRate.id}`,
          conversionRateFormData,
        );
        setMessage("Conversion rate updated successfully");
      } else {
        await api.post("/currency-conversion-rates", conversionRateFormData);
        setMessage("Conversion rate created successfully");
      }
      setShowConversionRateModal(false);
      setEditingConversionRate(null);
      setConversionRateFormData({
        year: new Date().getFullYear(),
        from_currency: "USD",
        to_currency: "CAD",
        conversion_rate: "",
        is_active: true,
      });
      fetchConversionRates();
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving conversion rate:", error);
      setMessage(
        error.response?.data?.message || "Error saving conversion rate",
      );
    }
  };

  const handleDeleteConversionRate = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this conversion rate?")
    ) {
      try {
        await api.delete(`/currency-conversion-rates/${id}`);
        setMessage("Conversion rate deleted successfully");
        fetchConversionRates();
        setTimeout(() => setMessage(""), 3000);
      } catch (error) {
        console.error("Error deleting conversion rate:", error);
        setMessage("Error deleting conversion rate");
      }
    }
  };

  const handleEditConversionRate = (rate) => {
    setEditingConversionRate(rate);
    setConversionRateFormData({
      year: rate.year,
      from_currency: rate.fromCurrency,
      to_currency: rate.toCurrency,
      conversion_rate: rate.conversionRate,
      is_active: rate.isActive,
    });
    setShowConversionRateModal(true);
  };

  const getConversionRateIsActive = (rate) =>
    rate.isActive !== undefined ? rate.isActive : rate.is_active !== false;

  const handleToggleConversionRateStatus = async (rate) => {
    try {
      await api.put(`/currency-conversion-rates/${rate.id}`, {
        is_active: !getConversionRateIsActive(rate),
      });
      fetchConversionRates();
      setMessage("Conversion rate status updated successfully");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling conversion rate status:", error);
      setMessage(
        error.response?.data?.message ||
          "Error updating conversion rate status",
      );
    }
  };

  const handleAddConversionRate = () => {
    setEditingConversionRate(null);
    setConversionRateFormData({
      year: new Date().getFullYear(),
      from_currency: "USD",
      to_currency: "CAD",
      conversion_rate: "",
      is_active: true,
    });
    setShowConversionRateModal(true);
  };

  // Currency & Units tab renderer with Conversion Rates
  const renderCurrencyUnitsTab = () => {
    const categorySettings = settings["currency_units"] || {};
    const settingsKeys = Object.keys(categorySettings);
    const defaultCurrencyKey = settingsKeys.includes("default_currency")
      ? "default_currency"
      : settingsKeys.includes("currency")
        ? "currency"
        : null;
    const preferredUnitKey = settingsKeys.includes("preferred_unit")
      ? "preferred_unit"
      : settingsKeys.includes("preferred_units")
        ? "preferred_units"
        : null;
    const remainingKeys = settingsKeys.filter(
      (key) => key !== defaultCurrencyKey && key !== preferredUnitKey,
    );

    return (
      <div className="space-y-6">
        {/* Default Currency & Preferred Unit Settings */}
        {settingsKeys.length > 0 && (
          <div className="space-y-4">
            {(defaultCurrencyKey || preferredUnitKey) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {defaultCurrencyKey &&
                  renderSettingField(
                    defaultCurrencyKey,
                    categorySettings[defaultCurrencyKey],
                  )}
                {preferredUnitKey &&
                  renderSettingField(
                    preferredUnitKey,
                    categorySettings[preferredUnitKey],
                  )}
              </div>
            )}
            {remainingKeys.map((key) =>
              renderSettingField(key, categorySettings[key]),
            )}
          </div>
        )}

        {/* Currency Conversion Rates Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-gray-900">
                  Currency Conversion Rates
                </h3>
                <InlineFieldTooltip
                  page="settings"
                  section="currency_units"
                  fieldKey="currency_conversion_rates.section_help"
                />
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Manage USD to CAD conversion rates by year. Used for cost
                calculations when materials are entered in USD.
              </p>
            </div>
            <button
              onClick={handleAddConversionRate}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Conversion Rate
            </button>
          </div>

          {conversionRatesLoading ? (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                className="text-2xl text-gray-400 mb-4"
              />
              <p className="text-gray-500">Loading conversion rates...</p>
            </div>
          ) : conversionRates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>
                No conversion rates configured. Click "Add Conversion Rate" to
                add one.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      From
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {conversionRates
                    .sort((a, b) => b.year - a.year)
                    .map((rate) => (
                      <tr key={rate.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {rate.year}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rate.fromCurrency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rate.toCurrency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rate.conversionRate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={getConversionRateIsActive(rate)}
                            onClick={() =>
                              handleToggleConversionRateStatus(rate)
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${getConversionRateIsActive(rate) ? "bg-blue-600" : "bg-gray-300"}`}
                            title="Toggle active status"
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${getConversionRateIsActive(rate) ? "translate-x-6" : "translate-x-1"}`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditConversionRate(rate)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Edit conversion rate"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteConversionRate(rate.id)
                              }
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete conversion rate"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderD610DegradationRateTab = () => {
    const categorySettings = settings.d610_degradation_rate || {};
    const matrixSetting = categorySettings[D610_DEGRADATION_RATE_SETTING_KEY];
    const defaultBandSetting =
      categorySettings[D610_DEGRADATION_RATE_DEFAULT_BAND_SETTING_KEY];
    const scoreMetadataSetting = categorySettings[D610_SCORE_METADATA_SETTING_KEY];
    const hasPersistedMatrixSetting = Boolean(matrixSetting);
    const hasPersistedDefaultBandSetting = Boolean(defaultBandSetting);
    const hasPersistedScoreMetadataSetting = Boolean(scoreMetadataSetting);
    const hasPersistedSettings =
      hasPersistedMatrixSetting &&
      hasPersistedDefaultBandSetting &&
      hasPersistedScoreMetadataSetting;
    const matrix = normalizeD610DegradationRateMatrix(matrixSetting?.value);
    const scoreMetadata = normalizeD610ScoreMetadataMatrix(
      scoreMetadataSetting?.value,
    );
    const scoreMetadataByScore = new Map(
      scoreMetadata.map((row) => [row.score, row]),
    );
    const d610Rows = matrix.map((row) => ({
      ...row,
      ...(scoreMetadataByScore.get(row.score) || {}),
    }));
    const defaultBand = normalizeD610DegradationRateDefaultBand(
      defaultBandSetting?.value,
    );

    return (
      <div className="space-y-4">
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              D610 Degradation Rate
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Configure the lower, mid, and upper degradation-rate bands,
              labels, abbreviations, and colours for each D610 score.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !hasPersistedSettings}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Save D610 Settings
              </>
            )}
          </button>
        </div>

        {!hasPersistedSettings && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Run the latest backend migrations to create all D610 degradation
            rate settings before saving changes.
          </div>
        )}

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    D610 Score
                  </th>
                  {D610_DEGRADATION_RATE_BANDS.map((band) => (
                    <th
                      key={band}
                      className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span>{band}</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={defaultBand === band}
                          aria-label={`Set ${band} as default D610 degradation band`}
                          title={`Set ${band} as default`}
                          disabled={!hasPersistedSettings || saving}
                          onClick={() => handleD610DefaultBandChange(band)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${defaultBand === band ? "bg-blue-600" : "bg-gray-300"} ${!hasPersistedSettings || saving ? "cursor-not-allowed opacity-50" : ""}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${defaultBand === band ? "translate-x-6" : "translate-x-1"}`}
                          />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    SSLG Grade
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Budget App Name
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Budget App. Abbreviation
                  </th>
                  <th className="whitespace-nowrap px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Colour
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {d610Rows.map((row) => (
                  <tr key={row.score}>
                    <td className="whitespace-nowrap bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                      {row.score}
                    </td>
                    {[
                      ["lower", row.lower],
                      ["mid", row.mid],
                      ["upper", row.upper],
                    ].map(([field, value]) => (
                      <td key={field} className="px-4 py-1">
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={value}
                          disabled={!hasPersistedSettings}
                          onChange={(e) =>
                            handleD610DegradationRateChange(
                              row.score,
                              field,
                              e.target.value === "" ? 0 : Number(e.target.value),
                            )
                          }
                          className="h-9 w-full rounded-md border border-gray-300 px-3 py-1 text-right text-sm leading-tight focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </td>
                    ))}
                    <td className="px-4 py-1">
                      <input
                        type="text"
                        value={row.sslgGrade}
                        disabled={!hasPersistedSettings}
                        onChange={(e) =>
                          handleD610ScoreMetadataChange(
                            row.score,
                            "sslgGrade",
                            e.target.value,
                          )
                        }
                        className="h-9 w-full rounded-md border border-gray-300 px-3 py-1 text-sm leading-tight focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </td>
                    <td className="px-4 py-1">
                      <input
                        type="text"
                        value={row.budgetAppName}
                        disabled={!hasPersistedSettings}
                        onChange={(e) =>
                          handleD610ScoreMetadataChange(
                            row.score,
                            "budgetAppName",
                            e.target.value,
                          )
                        }
                        className="h-9 min-w-[160px] w-full rounded-md border border-gray-300 px-3 py-1 text-sm leading-tight focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </td>
                    <td className="px-4 py-1">
                      <input
                        type="text"
                        value={row.budgetAppAbbreviation}
                        disabled={!hasPersistedSettings}
                        onChange={(e) =>
                          handleD610ScoreMetadataChange(
                            row.score,
                            "budgetAppAbbreviation",
                            e.target.value,
                          )
                        }
                        className="h-9 min-w-[150px] w-full rounded-md border border-gray-300 px-3 py-1 text-sm leading-tight focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </td>
                    <td className="px-4 py-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={row.color}
                          disabled={!hasPersistedSettings}
                          onChange={(e) =>
                            handleD610ScoreMetadataChange(
                              row.score,
                              "color",
                              e.target.value.toUpperCase(),
                            )
                          }
                          className="h-9 w-14 rounded-md border border-gray-300 bg-white p-1 disabled:cursor-not-allowed disabled:bg-gray-50"
                        />
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                          {row.color}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // Dynamic configuration tab renderers
  const renderCoatingSystemsTab = () => {
    // Ensure coatingSystems is an array
    const systems = Array.isArray(coatingSystems) ? coatingSystems : [];

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Coating Systems Configuration
            </h3>
            <p className="text-sm text-gray-600">
              Manage coating system definitions and sequences
            </p>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              setCoatingFormData({
                system_code: "",
                system_name: "",
                description: "",
                coat_sequence: ["prime", "topcoat"],
                is_active: true,
              });
              setShowCoatingModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Coating System
          </button>
        </div>

        {systems.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              className="text-2xl text-gray-400 mb-4"
            />
            <p className="text-gray-500">Loading coating systems...</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    System Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    System Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coat Sequence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {systems.map((system) => (
                  <tr key={system.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {system.system_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {system.system_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(system.coat_sequence)
                          ? system.coat_sequence
                          : JSON.parse(system.coat_sequence || "[]")
                        ).map((coat, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {coat}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          system.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {system.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            console.log(
                              "Edit button clicked for system:",
                              system.system_code,
                            );
                            setEditingItem(system);
                            setCoatingFormData({
                              system_code: system.system_code,
                              system_name: system.system_name,
                              description: system.description || "",
                              coat_sequence: Array.isArray(system.coat_sequence)
                                ? system.coat_sequence
                                : JSON.parse(system.coat_sequence || "[]"),
                              is_active: system.is_active,
                            });
                            setShowCoatingModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Edit coating system"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => {
                            console.log(
                              "Delete button clicked for system:",
                              system.system_code,
                            );
                            handleDeleteCoatingSystem(system.id);
                          }}
                          className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                          title="Delete coating system"
                        >
                          <FontAwesomeIcon icon={faTrashAlt} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderDegradationModelsTab = () => {
    // Ensure degradationParams is an array
    const params = Array.isArray(degradationParams) ? degradationParams : [];

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            Degradation Parameters Configuration
          </h3>
          <p className="text-sm text-gray-600">
            Manage degradation formula parameters for different corrosion
            categories
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="fas fa-chart-line text-blue-600"></i>
            <h4 className="text-lg font-semibold text-blue-800">
              Degradation Formula: D(t) = A × e^(B × t)
            </h4>
          </div>
          <p className="text-sm text-blue-700">
            Where: A = coefficient, B = growth rate, t = years since last paint
          </p>
        </div>

        {params.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-2xl text-gray-400 mb-4">⏳</div>
            <p className="text-gray-500">Loading degradation parameters...</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Corrosion Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coefficient A
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Growth Rate B
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Speed
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sample (5 years)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {params.map((param) => (
                  <tr key={param.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {param.corrosion_category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {param.coefficient_a}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {param.growth_rate_b}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          param.degradation_speed === "slowest"
                            ? "bg-green-100 text-green-800"
                            : param.degradation_speed === "medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : param.degradation_speed === "faster"
                                ? "bg-orange-100 text-orange-800"
                                : "bg-red-100 text-red-800"
                        }`}
                      >
                        {param.degradation_speed}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">🧮</span>
                        {(
                          param.coefficient_a *
                          Math.exp(param.growth_rate_b * 5)
                        ).toFixed(6)}
                        %
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            console.log(
                              "Edit degradation param clicked for:",
                              param.corrosion_category,
                            );
                            handleEditDegradationParam(param);
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                          title="Edit degradation parameters"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    // Handle special tabs
    if (activeTab === "currency_units") {
      return renderCurrencyUnitsTab();
    } else if (activeTab === "system_lists") {
      return renderSystemListsTab();
    } else if (activeTab === "d610_degradation_rate") {
      return renderD610DegradationRateTab();
    } else if (activeTab === "coating_systems") {
      return renderCoatingSystemsTab();
    } else if (activeTab === "degradation_models") {
      return renderDegradationModelsTab();
    }

    // Handle regular settings tabs
    const categorySettings = settings[activeTab] || {};
    const settingsKeys = Object.keys(categorySettings);

    if (settingsKeys.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <i className="fas fa-cog text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-500">
            No settings available for this category
          </p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {settingsKeys.map((key) =>
          renderSettingField(key, categorySettings[key]),
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  const showSaveButton =
    activeTab !== "system_lists" &&
    activeTab !== "d610_degradation_rate" &&
    Object.keys(settings).includes(activeTab);

  const containerWidthClass =
    activeTab === "d610_degradation_rate"
      ? "max-w-[1500px]"
      : "max-w-5xl";

  return (
    <div className={`${containerWidthClass} mx-auto p-4 md:p-5`}>
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-start gap-2">
          <p className="text-gray-600">
            Configure system-wide defaults and parameters for bridge maintenance
            budgeting
          </p>
          <InlineFieldTooltip
            page="settings"
            section="page_description"
            fieldKey="settings_overview"
          />
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            message.includes("Error")
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-green-50 border-green-200 text-green-800"
          }`}
        >
          <div className="flex items-center">
            <i
              className={`fas ${message.includes("Error") ? "fa-exclamation-circle" : "fa-check-circle"} mr-2`}
            ></i>
            {message}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas fa-${tab.icon}`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="mb-8">{renderTabContent()}</div>

      {/* Actions */}
      {showSaveButton && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Save Changes
              </>
            )}
          </button>
        </div>
      )}

      {showSystemListCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-[560px] shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Create New System List
              </h3>
              <button
                onClick={() => setShowSystemListCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label: "List Name",
                  fieldKey: "system_lists.create_modal.list_name",
                })}
                <input
                  type="text"
                  value={newSystemListName}
                  onChange={(e) => setNewSystemListName(e.target.value)}
                  placeholder="e.g., Material Type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "Values (one per line)",
                  fieldKey: "system_lists.create_modal.values",
                })}
                <textarea
                  value={systemListValueInput}
                  onChange={(e) => setSystemListValueInput(e.target.value)}
                  rows={5}
                  placeholder={"Prime\nStripecoat\nMidcoat\nTopcoat"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "Attributes (one per line)",
                  fieldKey: "system_lists.create_modal.attributes",
                })}
                <textarea
                  value={newSystemListAttributeInput}
                  onChange={(e) =>
                    setNewSystemListAttributeInput(e.target.value)
                  }
                  rows={4}
                  placeholder={"Thickness\nCoverage\nManufacturer"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {normalizeListName(newSystemListName) === "material type" && (
                  <p className="text-xs text-gray-500 mt-1">
                    Material Type will always include the "color" attribute.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowSystemListCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSystemList}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Create List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSystemListRenameModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-5 border w-11/12 md:w-[520px] shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Rename List</h3>
              <button
                onClick={() => setShowSystemListRenameModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label: "Current Name",
                  fieldKey: "system_lists.rename_modal.current_name",
                })}
                <input
                  type="text"
                  value={renameSourceListName}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md"
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "New Name",
                  fieldKey: "system_lists.rename_modal.new_name",
                })}
                <input
                  type="text"
                  value={renameTargetListName}
                  onChange={(e) => setRenameTargetListName(e.target.value)}
                  placeholder="Enter new list name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowSystemListRenameModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRenameSystemList}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSystemListSchemaModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-24 mx-auto p-5 border w-11/12 md:w-[560px] shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Edit Attributes for {activeSystemList}
              </h3>
              <button
                onClick={() => setShowSystemListSchemaModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label: "Attributes (one per line)",
                  fieldKey: "system_lists.schema_modal.attributes",
                })}
                <textarea
                  value={systemListSchemaInput}
                  onChange={(e) => setSystemListSchemaInput(e.target.value)}
                  rows={6}
                  placeholder={"Thickness\nCoverage\nManufacturer"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
                {isMaterialTypeList && (
                  <p className="text-xs text-gray-500 mt-1">
                    Material Type keeps the "color" attribute automatically.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setShowSystemListSchemaModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSystemListSchema}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Save Attributes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSystemListValueModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-[560px] shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {systemListValueModalMode === "edit"
                  ? `Edit Value in ${activeSystemList}`
                  : `Add Values to ${activeSystemList}`}
              </h3>
              <button
                onClick={() => {
                  setShowSystemListValueModal(false);
                  setEditingSystemListValue(null);
                  setSystemListValueInput("");
                  setSystemListPropertyValues({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label:
                    systemListValueModalMode === "edit"
                      ? "Value"
                      : "Values (one per line)",
                  fieldKey: "system_lists.value_modal.value",
                })}
                {systemListValueModalMode === "edit" ? (
                  <input
                    type="text"
                    value={systemListValueInput}
                    onChange={(e) => setSystemListValueInput(e.target.value)}
                    placeholder="Enter value"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                ) : (
                  <textarea
                    value={systemListValueInput}
                    onChange={(e) => setSystemListValueInput(e.target.value)}
                    rows={5}
                    placeholder={"Value 1\nValue 2\nValue 3"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                )}
              </div>

              {activeSystemListAttributes.length > 0 && (
                <div>
                  <div className="mb-1 flex items-center justify-between">
                    {renderLabelWithTooltip({
                      label: "Attribute Values",
                      fieldKey: "system_lists.value_modal.attributes",
                    })}
                  </div>

                  <div className="space-y-2">
                    {activeSystemListAttributes.map((attributeKey) => (
                      <div
                        key={attributeKey}
                        className="grid grid-cols-12 gap-2"
                      >
                        <input
                          type="text"
                          value={attributeKey}
                          readOnly
                          className="col-span-4 px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-gray-600"
                        />
                        {String(attributeKey).toLowerCase() === "color" ? (
                          <input
                            type="color"
                            value={
                              String(
                                systemListPropertyValues[attributeKey] || "",
                              ) || "#2563eb"
                            }
                            onChange={(e) =>
                              setSystemListPropertyValues((prev) => ({
                                ...prev,
                                [attributeKey]: e.target.value,
                              }))
                            }
                            className="col-span-8 h-10 p-1 border border-gray-300 rounded-md"
                          />
                        ) : (
                          <input
                            type="text"
                            value={String(
                              systemListPropertyValues[attributeKey] || "",
                            )}
                            onChange={(e) =>
                              setSystemListPropertyValues((prev) => ({
                                ...prev,
                                [attributeKey]: e.target.value,
                              }))
                            }
                            placeholder={`Enter ${attributeKey}`}
                            className="col-span-8 px-3 py-2 border border-gray-300 rounded-md"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                {renderLabelWithTooltip({
                  label: "Sort Order",
                  fieldKey: "system_lists.value_modal.sort_order",
                })}
                <input
                  type="number"
                  step="1"
                  value={newSystemListSortOrder}
                  onChange={(e) =>
                    setNewSystemListSortOrder(
                      e.target.value === ""
                        ? ""
                        : parseSortOrder(e.target.value, 100),
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowSystemListValueModal(false);
                    setEditingSystemListValue(null);
                    setSystemListValueInput("");
                    setNewSystemListSortOrder(100);
                    setSystemListPropertyValues({});
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitSystemListValueModal}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {systemListValueModalMode === "edit" ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Currency Conversion Rate Modal */}
      {showConversionRateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingConversionRate
                  ? "Edit Conversion Rate"
                  : "Add Conversion Rate"}
              </h3>
              <button
                onClick={() => {
                  setShowConversionRateModal(false);
                  setEditingConversionRate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label: "Year *",
                  fieldKey: "currency_units.conversion_rates.year",
                })}
                <input
                  type="number"
                  value={conversionRateFormData.year}
                  onChange={(e) =>
                    setConversionRateFormData({
                      ...conversionRateFormData,
                      year: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min="2000"
                  max="2100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  {renderLabelWithTooltip({
                    label: "From Currency *",
                    fieldKey: "currency_units.conversion_rates.from_currency",
                  })}
                  <select
                    value={conversionRateFormData.from_currency}
                    onChange={(e) =>
                      setConversionRateFormData({
                        ...conversionRateFormData,
                        from_currency: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div>
                  {renderLabelWithTooltip({
                    label: "To Currency *",
                    fieldKey: "currency_units.conversion_rates.to_currency",
                  })}
                  <select
                    value={conversionRateFormData.to_currency}
                    onChange={(e) =>
                      setConversionRateFormData({
                        ...conversionRateFormData,
                        to_currency: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "Conversion Rate *",
                  fieldKey: "currency_units.conversion_rates.conversion_rate",
                })}
                <input
                  type="number"
                  step="0.0001"
                  value={conversionRateFormData.conversion_rate}
                  onChange={(e) =>
                    setConversionRateFormData({
                      ...conversionRateFormData,
                      conversion_rate: parseFloat(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="e.g., 1.35"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {conversionRateFormData.from_currency} 1 ={" "}
                  {conversionRateFormData.to_currency}{" "}
                  {conversionRateFormData.conversion_rate || "?"}
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_rate"
                  checked={conversionRateFormData.is_active}
                  onChange={(e) =>
                    setConversionRateFormData({
                      ...conversionRateFormData,
                      is_active: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_active_rate"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Active
                </label>
                <InlineFieldTooltip
                  page="settings"
                  section={activeTab}
                  fieldKey="currency_units.conversion_rates.is_active"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowConversionRateModal(false);
                    setEditingConversionRate(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConversionRate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <i className="fas fa-save"></i>
                  {editingConversionRate ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coating System Modal */}
      {showCoatingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingItem ? "Edit Coating System" : "Add New Coating System"}
              </h3>
              <button
                onClick={() => setShowCoatingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                {renderLabelWithTooltip({
                  label: "System Code *",
                  fieldKey: "coating_systems.modal.system_code",
                  section: "coating_systems",
                })}
                <input
                  type="text"
                  value={coatingFormData.system_code}
                  onChange={(e) =>
                    setCoatingFormData({
                      ...coatingFormData,
                      system_code: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "System Name *",
                  fieldKey: "coating_systems.modal.system_name",
                  section: "coating_systems",
                })}
                <input
                  type="text"
                  value={coatingFormData.system_name}
                  onChange={(e) =>
                    setCoatingFormData({
                      ...coatingFormData,
                      system_name: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "Description",
                  fieldKey: "coating_systems.modal.description",
                  section: "coating_systems",
                })}
                <textarea
                  value={coatingFormData.description}
                  onChange={(e) =>
                    setCoatingFormData({
                      ...coatingFormData,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                {renderLabelWithTooltip({
                  label: "Coat Sequence *",
                  fieldKey: "coating_systems.modal.coat_sequence",
                  section: "coating_systems",
                })}
                <div className="space-y-2">
                  {coatingFormData.coat_sequence.map((coat, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={coat}
                        onChange={(e) => {
                          const newSequence = [
                            ...coatingFormData.coat_sequence,
                          ];
                          newSequence[index] = e.target.value;
                          setCoatingFormData({
                            ...coatingFormData,
                            coat_sequence: newSequence,
                          });
                        }}
                        placeholder="Coat type (e.g., prime, stripe, midcoat, topcoat)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      {coatingFormData.coat_sequence.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSequence =
                              coatingFormData.coat_sequence.filter(
                                (_, i) => i !== index,
                              );
                            setCoatingFormData({
                              ...coatingFormData,
                              coat_sequence: newSequence,
                            });
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setCoatingFormData({
                        ...coatingFormData,
                        coat_sequence: [...coatingFormData.coat_sequence, ""],
                      });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Coat Type
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={coatingFormData.is_active}
                  onChange={(e) =>
                    setCoatingFormData({
                      ...coatingFormData,
                      is_active: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_active"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Active
                </label>
                <InlineFieldTooltip
                  page="settings"
                  section="coating_systems"
                  fieldKey="coating_systems.modal.is_active"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCoatingModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCoatingSystem}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <i className="fas fa-save"></i>
                  {editingItem ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {process.env.REACT_APP_VERSION && (
        <p className="mt-6 text-xs text-gray-400">
          App version: {process.env.REACT_APP_VERSION}
        </p>
      )}
    </div>
  );
};

export default Settings;
